package songwritersnotebook.app.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.data.SongVersion
import songwritersnotebook.app.data.VersionType
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun VersionHistorySheet(
    versions: List<SongVersion>,
    isLoading: Boolean,
    onDismiss: () -> Unit,
    onPreview: (SongVersion) -> Unit,
    onRestore: (SongVersion) -> Unit,
    onDelete: (SongVersion) -> Unit,
    onSaveSnapshot: (String) -> Unit,
    onUpdateNotes: (SongVersion, String) -> Unit,
    isStudio: Boolean // To differentiate tiers if needed visually
) {
    var showSnapshotDialog by remember { mutableStateOf(false) }
    var selectedVersion by remember { mutableStateOf<SongVersion?>(null) }
    var showRestoreConfirm by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var showEditNotes by remember { mutableStateOf(false) }

    if (showSnapshotDialog) {
        var notes by remember { mutableStateOf("") }
        AlertDialog(
            onDismissRequest = { showSnapshotDialog = false },
            title = { Text("Save New Version") },
            text = {
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Version Notes (Optional)") },
                    placeholder = { Text("e.g. Before changing the bridge") },
                    modifier = Modifier.fillMaxWidth()
                )
            },
            confirmButton = {
                Button(onClick = {
                    onSaveSnapshot(notes)
                    showSnapshotDialog = false
                }) {
                    Text("Save")
                }
            },
            dismissButton = { TextButton(onClick = { showSnapshotDialog = false }) { Text("Cancel") } }
        )
    }

    if (showRestoreConfirm && selectedVersion != null) {
        AlertDialog(
            onDismissRequest = { showRestoreConfirm = false },
            title = { Text("Restore Version ${selectedVersion!!.versionNumber}?") },
            text = { Text("This will overwrite your current song state with the data from this version. This action can be undone by restoring the current version (if you save it first).") },
            confirmButton = {
                Button(onClick = {
                    onRestore(selectedVersion!!)
                    showRestoreConfirm = false
                }) {
                    Text("Restore")
                }
            },
            dismissButton = { TextButton(onClick = { showRestoreConfirm = false }) { Text("Cancel") } }
        )
    }

    if (showDeleteConfirm && selectedVersion != null) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text("Delete Version ${selectedVersion!!.versionNumber}?") },
            text = { Text("Are you sure you want to delete this version snapshot? This cannot be undone.") },
            confirmButton = {
                Button(
                    onClick = {
                        onDelete(selectedVersion!!)
                        showDeleteConfirm = false
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Delete")
                }
            },
            dismissButton = { TextButton(onClick = { showDeleteConfirm = false }) { Text("Cancel") } }
        )
    }

    if (showEditNotes && selectedVersion != null) {
        var notes by remember(selectedVersion) { mutableStateOf(selectedVersion!!.notes) }
        AlertDialog(
            onDismissRequest = { showEditNotes = false },
            title = { Text("Edit Notes") },
            text = {
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Notes") },
                    modifier = Modifier.fillMaxWidth()
                )
            },
            confirmButton = {
                Button(onClick = {
                    onUpdateNotes(selectedVersion!!, notes)
                    showEditNotes = false
                }) {
                    Text("Save")
                }
            },
            dismissButton = { TextButton(onClick = { showEditNotes = false }) { Text("Cancel") } }
        )
    }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .navigationBarsPadding()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Version History", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                Button(onClick = { showSnapshotDialog = true }) {
                    Icon(Icons.Default.Add, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Save Version")
                }
            }
            
            Spacer(Modifier.height(16.dp))

            if (isLoading) {
                Box(Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            } else if (versions.isEmpty()) {
                Box(Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                    Text("No history saved yet.", color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            } else {
                LazyColumn(
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.weight(1f, fill = false)
                ) {
                    items(versions) { version ->
                        VersionItem(
                            version = version,
                            onPreview = { onPreview(version) },
                            onRestore = {
                                selectedVersion = version
                                showRestoreConfirm = true
                            },
                            onEditNotes = {
                                selectedVersion = version
                                showEditNotes = true
                            },
                            onDelete = {
                                selectedVersion = version
                                showDeleteConfirm = true
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun VersionItem(
    version: SongVersion,
    onPreview: () -> Unit,
    onRestore: () -> Unit,
    onEditNotes: () -> Unit,
    onDelete: () -> Unit
) {
    val dateText = remember(version.timestamp) {
        val instant = Instant.fromEpochMilliseconds(version.timestamp)
        val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val monthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        val month = monthNames[dt.monthNumber - 1]
        val amPm = if (dt.hour >= 12) "PM" else "AM"
        val hour12 = if (dt.hour > 12) dt.hour - 12 else if (dt.hour == 0) 12 else dt.hour
        val minute = dt.minute.toString().padStart(2, '0')
        "$month ${dt.dayOfMonth}, ${dt.year}, $hour12:$minute $amPm"
    }

    val stats = remember(version.songData) {
        val lineCount = version.songData.sections.sumOf { it.lines.size }
        val chordCount = version.songData.chordDefinitions.size
        val recordingCount = version.songData.recordings.values.sumOf { it.size }
        "Lines: $lineCount \u2022 Chords: $chordCount \u2022 Audio: $recordingCount"
    }

    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(12.dp)) {
            Row(
                verticalAlignment = Alignment.Top,
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            "Version ${version.versionNumber}",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                        Spacer(Modifier.width(8.dp))
                        if (version.type == VersionType.CHECKPOINT) {
                            Badge(containerColor = MaterialTheme.colorScheme.secondaryContainer) {
                                Text("Checkpoint", color = MaterialTheme.colorScheme.onSecondaryContainer, fontSize = 10.sp)
                            }
                        } else {
                            Badge(containerColor = MaterialTheme.colorScheme.primaryContainer) {
                                Text("Manual", color = MaterialTheme.colorScheme.onPrimaryContainer, fontSize = 10.sp)
                            }
                        }
                    }
                    
                    Text(
                        text = dateText,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    
                    Text(
                        text = stats,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        modifier = Modifier.padding(top = 2.dp)
                    )
                }
                
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onEditNotes, modifier = Modifier.size(32.dp)) {
                        Icon(Icons.Default.Edit, "Edit Notes", modifier = Modifier.size(18.dp), tint = MaterialTheme.colorScheme.primary)
                    }
                    IconButton(onClick = onDelete, modifier = Modifier.size(32.dp)) {
                        Icon(Icons.Default.Delete, "Delete", modifier = Modifier.size(18.dp), tint = MaterialTheme.colorScheme.error)
                    }
                }
            }
            
            if (version.notes.isNotBlank()) {
                Spacer(Modifier.height(8.dp))
                Text(
                    text = version.notes,
                    style = MaterialTheme.typography.bodyMedium,
                    fontStyle = androidx.compose.ui.text.font.FontStyle.Italic,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            
            Spacer(Modifier.height(12.dp))
            
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedButton(
                    onClick = onPreview,
                    modifier = Modifier.weight(1f),
                    contentPadding = PaddingValues(0.dp)
                ) {
                    Icon(Icons.Default.Visibility, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Preview")
                }
                Button(
                    onClick = onRestore,
                    modifier = Modifier.weight(1f),
                    contentPadding = PaddingValues(0.dp)
                ) {
                    Icon(Icons.Default.Restore, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Restore")
                }
            }
        }
    }
}
